import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router";
import {
  MdDashboard,
  MdKeyboardArrowDown,
  MdMessage,
} from "react-icons/md";
import {
  FaBuffer,
  FaCalculator,
  FaTasks,
} from "react-icons/fa";
import { TbReportAnalytics } from "react-icons/tb";
import { ARABIC_LABELS } from "../../labels/Business/arabic";
import "./Sidebar.css";
const accessControlPaths = [
  "/system/accesscontrol/changepassword",
];
const managementPaths = [
  "/system/management/serviceControl",
  "/system/subsidy/viewSubsidy",
  "/system/subsidy/jobList",
  "/system/subsidy/addJob",
  "/system/business/viewbusinesspartner",
  "/system/customer/viewCustomers",
  "/system/customer/customerDetail",
  "/system/invoice/viewInvoices",
  "/system/match/approveMatch",
  "/system/contract/printContractActive",
  "/system/contract/comprehensive",
  "/system/contract/addComcontract",
  "/system/customer/searchPc",
];
const workflowPaths = [
  "/system/workflow/workflowList",
];
/*
 * Operation pages
 */
const operationPaths = [
  "/system/myactivity/myactivityList/1",
  "/system/myactivity/myactivityList/2",
  "/system/myactivity/myactivityList/4",
  "/system/myactivity/myactivityList/5",
];
const reportPaths = [
  "/system/reports/paymentBp",
  "/system/reports/paymentPc",
];
const isPathActive = (pathname, paths) => {
  return paths.some((path) =>
    pathname.startsWith(path)
  );
};
const getStoredCustomerId = () => {
  try {
    const encodedCustomerId =
      localStorage.getItem("customerId");
    if (!encodedCustomerId) {
      return null;
    }
    const decodedCustomerId = Number(
      atob(encodedCustomerId)
    );
    return Number.isFinite(decodedCustomerId)
      ? decodedCustomerId
      : null;
  } catch {
    return null;
  }
};
const SidebarInternalMain = () => {
  const labels = ARABIC_LABELS;
  const location = useLocation();
  const [openSection, setOpenSection] =
    useState(null);
  const [customerId, setCustomerId] =
    useState(getStoredCustomerId);
  const canViewAccessControl = [
    42090,
    1654351,
    1423355
  ].includes(customerId);
  const isAccessControlActive =
    isPathActive(
      location.pathname,
      accessControlPaths
    );
  const isManagementActive =
    isPathActive(
      location.pathname,
      managementPaths
    );
  const isWorkflowActive =
    isPathActive(
      location.pathname,
      workflowPaths
    );
  const isOperationActive =
    isPathActive(
      location.pathname,
      operationPaths
    );
  const isReportsActive =
    isPathActive(
      location.pathname,
      reportPaths
    );
  /*
   * Keep the related parent menu open
   * whenever the current page belongs
   * to that section.
   */
  useEffect(() => {
    if (isAccessControlActive) {
      setOpenSection("access-control");
      return;
    }
    if (isManagementActive) {
      setOpenSection("management");
      return;
    }
    if (isWorkflowActive) {
      setOpenSection("workflow");
      return;
    }
    if (isOperationActive) {
      setOpenSection("operation");
      return;
    }
    if (isReportsActive) {
      setOpenSection("reports");
      return;
    }
    setOpenSection(null);
  }, [
    isAccessControlActive,
    isManagementActive,
    isWorkflowActive,
    isOperationActive,
    isReportsActive,
  ]);
  useEffect(() => {
    const updateCustomerId = () => {
      setCustomerId(
        getStoredCustomerId()
      );
    };
    window.addEventListener(
      "storage",
      updateCustomerId
    );
    window.addEventListener(
      "internal-profile-loaded",
      updateCustomerId
    );
    return () => {
      window.removeEventListener(
        "storage",
        updateCustomerId
      );
      window.removeEventListener(
        "internal-profile-loaded",
        updateCustomerId
      );
    };
  }, []);
  const toggleSection = (section) => {
    setOpenSection((currentSection) =>
      currentSection === section
        ? null
        : section
    );
  };
  return (
    <aside
      className="dga-sidebar"
      aria-label="القائمة الرئيسية للمستخدم الداخلي"
    >
      <nav className="dga-sidebar__navigation">
        <ul className="dga-sidebar__list">
          {/* Dashboard */}
          <li className="dga-sidebar__item">
            <NavLink
              to="/system/dashboard"
              className={({ isActive }) =>
                `dga-sidebar__link ${
                  isActive ? "active" : ""
                }`
              }
            >
              <span className="dga-sidebar__icon">
                <MdDashboard />
              </span>
              <span className="dga-sidebar__label">
                {labels.dashboard}
              </span>
            </NavLink>
          </li>
          {/* Access Control */}
          {canViewAccessControl && (
            <li
              className={`dga-sidebar__item dga-sidebar__item--expandable ${
                openSection ===
                "access-control"
                  ? "open"
                  : ""
              }`}
            >
              <button
                type="button"
                className={`dga-sidebar__link dga-sidebar__button ${
                  isAccessControlActive
                    ? "section-active"
                    : ""
                }`}
                onClick={() =>
                  toggleSection(
                    "access-control"
                  )
                }
                aria-expanded={
                  openSection ===
                  "access-control"
                }
              >
                <span className="dga-sidebar__icon">
                  <FaBuffer />
                </span>
                <span className="dga-sidebar__label">
                  {labels.accesscontrol}
                </span>
                <MdKeyboardArrowDown className="dga-sidebar__arrow" />
              </button>
              <div className="dga-sidebar__submenu-wrapper">
                <ul className="dga-sidebar__submenu">
                  <li>
                    <NavLink
                      to="/system/accesscontrol/changepassword"
                      className={({
                        isActive,
                      }) =>
                        isActive
                          ? "active"
                          : ""
                      }
                    >
                      {labels.userlist}
                    </NavLink>
                  </li>
                </ul>
              </div>
            </li>
          )}
          {/* Management */}
          <li
            className={`dga-sidebar__item dga-sidebar__item--expandable ${
              openSection === "management"
                ? "open"
                : ""
            }`}
          >
            <button
              type="button"
              className={`dga-sidebar__link dga-sidebar__button ${
                isManagementActive
                  ? "section-active"
                  : ""
              }`}
              onClick={() =>
                toggleSection("management")
              }
              aria-expanded={
                openSection === "management"
              }
            >
              <span className="dga-sidebar__icon">
                <MdMessage />
              </span>
              <span className="dga-sidebar__label">
                {labels.management}
              </span>
              <MdKeyboardArrowDown className="dga-sidebar__arrow" />
            </button>
            <div className="dga-sidebar__submenu-wrapper">
              <ul className="dga-sidebar__submenu">
                {canViewAccessControl && (
                  <li>
                    <NavLink to="/system/serviceControl">
                      التحكم في تقديم الطلبات والفواتير
                    </NavLink>
                  </li>
                )}

                <li>
                  <NavLink to="/system/subsidy/viewSubsidy">
                    {labels.contract}
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/system/business/viewbusinesspartner">
                    {labels.businesspartner}
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/system/customer/viewCustomers">
                    {labels.jobseeker}
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/system/invoice/viewInvoices">
                    {labels.invsection}
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/system/match/approveMatch">
                    {labels.approvenin}
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/system/contract/printContractActive">
                    {labels.printandactivecon}
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/system/contract/comprehensive">
                    {labels.comprecontract}
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/system/customer/searchPc">
                    {labels.searchpc}
                  </NavLink>
                </li>
              </ul>
            </div>
          </li>
          {/* Workflow */}
          <li
            className={`dga-sidebar__item dga-sidebar__item--expandable ${
              openSection === "workflow"
                ? "open"
                : ""
            }`}
          >
            <button
              type="button"
              className={`dga-sidebar__link dga-sidebar__button ${
                isWorkflowActive
                  ? "section-active"
                  : ""
              }`}
              onClick={() =>
                toggleSection("workflow")
              }
              aria-expanded={
                openSection === "workflow"
              }
            >
              <span className="dga-sidebar__icon">
                <FaCalculator />
              </span>
              <span className="dga-sidebar__label">
                {labels.workflow}
              </span>
              <MdKeyboardArrowDown className="dga-sidebar__arrow" />
            </button>
            <div className="dga-sidebar__submenu-wrapper">
              <ul className="dga-sidebar__submenu">
                <li>
                  <NavLink to="/system/workflow/workflowList">
                    {labels.workflowlist}
                  </NavLink>
                </li>
              </ul>
            </div>
          </li>
          {/* Operations */}
          <li
            className={`dga-sidebar__item dga-sidebar__item--expandable ${
              openSection === "operation"
                ? "open"
                : ""
            }`}
          >
            <button
              type="button"
              className={`dga-sidebar__link dga-sidebar__button ${
                isOperationActive
                  ? "section-active"
                  : ""
              }`}
              onClick={() =>
                toggleSection("operation")
              }
              aria-expanded={
                openSection === "operation"
              }
            >
              <span className="dga-sidebar__icon">
                <FaTasks />
              </span>
              <span className="dga-sidebar__label">
                {labels.operation}
              </span>
              <MdKeyboardArrowDown className="dga-sidebar__arrow" />
            </button>
            <div className="dga-sidebar__submenu-wrapper">
              <ul className="dga-sidebar__submenu">
                <li>
                  <NavLink to="/system/myactivity/myactivityList/1">
                    {labels.approvecontract}
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/system/myactivity/myactivityList/2">
                    {labels.approveinvoice}
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/system/myactivity/myactivityList/4">
                    {labels.approvebusiness}
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/system/myactivity/myactivityList/5">
                    {labels.approvepc}
                  </NavLink>
                </li>
              </ul>
            </div>
          </li>
          {/* Reports */}
          <li
            className={`dga-sidebar__item dga-sidebar__item--expandable ${
              openSection === "reports"
                ? "open"
                : ""
            }`}
          >
            <button
              type="button"
              className={`dga-sidebar__link dga-sidebar__button ${
                isReportsActive
                  ? "section-active"
                  : ""
              }`}
              onClick={() =>
                toggleSection("reports")
              }
              aria-expanded={
                openSection === "reports"
              }
            >
              <span className="dga-sidebar__icon">
                <TbReportAnalytics />
              </span>
              <span className="dga-sidebar__label">
                {labels.reports}
              </span>
              <MdKeyboardArrowDown className="dga-sidebar__arrow" />
            </button>
            <div className="dga-sidebar__submenu-wrapper">
              <ul className="dga-sidebar__submenu">
                <li>
                  <NavLink to="/system/reports/paymentBp">
                    {labels.bpreport}
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/system/reports/paymentPc">
                    {labels.pcreport}
                  </NavLink>
                </li>
              </ul>
            </div>
          </li>
        </ul>
      </nav>
    </aside>
  );
};
export default SidebarInternalMain;
